import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import {
  Briefcase,
  ChevronLeft,
  CircleAlert,
  Facebook,
  File as FileIcon,
  FilePlus,
  FileText,
  Film,
  Image as ImageIcon,
  Linkedin,
  LucideIcon,
  Mail,
  MessageCircle,
  MoreHorizontal,
  Music,
  Pencil,
  Phone,
  Send,
  Sparkles,
  Trash2,
  Twitter,
} from 'lucide-react';
import { DashboardLayout } from '../../components/dashboard';
import { stakeholderService } from '../../services/stakeholderService';
import { ConfidentialityPanel, LockMarker } from '../../features/confidentiality';
import type { Confidentiality } from '../../features/confidentiality';
import { Stakeholder, Assignment } from '../../types/stakeholder';
import { mediaService } from '../../services/mediaService';
import { assignmentService } from '../../services/assignmentService';
import { MediaAsset } from '../../types/media';
import { wikiService } from '../../services/wikiService';
import { ConfirmModal } from '../../components/common/ConfirmModal';
import { MessageModal } from '../../components/common/MessageModal';
import { IntelligenceReportGenerator } from '../../components/intelligence';
import { AuthImage } from '../../components/media/AuthImage';
import { WikiPage } from '../../types/wiki';
import { useAuth } from '../../contexts/AuthContext';
import { InfluenceLineChart } from '../../components/charts/InfluenceLineChart';
import { getEntityInfluenceData, InfluenceScore, InfluenceCategory } from '../../services/influencesService';
import { ReportGenerator } from '../../components/reports';
import { AssignmentManager } from '../../components/stakeholders/AssignmentManager';
import { WikiPageManager } from '../../components/wiki/WikiPageManager';
import { WikiExcerpt } from '../../components/wiki/WikiExcerpt';
import { StakeholderNewsTab } from './components/StakeholderNewsTab';
import { EntityMonitoringPanel } from '../../features/monitoring/components/EntityMonitoringPanel';
import { StakeholderIssuesSection } from './components/StakeholderIssuesSection';
import { StakeholderUpdates } from '../../features/government/components/StakeholderUpdates';
import { ProfileNetwork } from '../../features/intelligence/profile/ProfileNetwork';
import { AreasOfInterest } from '../../features/intelligence/profile/AreasOfInterest';
import { useTaxonomyColors } from '../../hooks/useTaxonomyColors';
import { asFlag, asOneOf, useUrlState } from '../../hooks/useUrlState';
import { useMissingMedia } from '../../utils/missingMedia';
import {
  Avatar,
  Button,
  Card,
  CategoryPill,
  EmptyState,
  IconButton,
  MEDIA_TONE,
  MediaKind,
  Menu,
  MenuItem,
  Page,
  Pill,
  SectionHeader,
  Skeleton,
  Spinner,
  statusTone,
  Tabs,
} from '../../components/ui';

function normalizeUrl(url: string): string {
  if (!url) return url;
  if (/^https?:\/\//i.test(url)) return url;
  return `https://${url}`;
}

function whatsappLink(value: string): string {
  const trimmed = value.trim();
  if (trimmed.startsWith('http')) return trimmed;
  const digits = trimmed.replace(/\D/g, '');
  return `https://wa.me/${digits}`;
}

function prettyUrl(url: string): string {
  try {
    const u = new URL(url);
    const host = u.host.replace(/^www\./i, '');
    let path = u.pathname || '';
    if (path.endsWith('/')) path = path.slice(0, -1);
    return path && path !== '/' ? `${host}${path}` : host;
  } catch {
    return url;
  }
}

function extractMediaIdsFromContent(content: string): string[] {
  if (!content) return [];
  const ids = new Set<string>();
  const re1 = /\/api\/media\/([a-zA-Z0-9-]+)\/(?:view|download)/g;
  let m: RegExpExecArray | null;
  while ((m = re1.exec(content)) !== null) ids.add(m[1]);
  const reMd = /\((https?:[^)]+\/api\/media\/([a-zA-Z0-9-]+)\/(?:view|download)[^)]*)\)/g;
  while ((m = reMd.exec(content)) !== null) ids.add(m[2]);
  return Array.from(ids);
}

const formatDate = (dateStr?: string): string => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
};

const formatFullDate = (dateStr?: string): string => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' });
};

const calculateDuration = (startDate: string, endDate?: string): string => {
  const start = new Date(startDate);
  const end = endDate ? new Date(endDate) : new Date();
  const months = (end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth());
  const years = Math.floor(months / 12);
  const remainingMonths = months % 12;
  if (years > 0 && remainingMonths > 0) return `${years}y ${remainingMonths}m`;
  if (years > 0) return `${years}y`;
  if (remainingMonths > 0) return `${remainingMonths}m`;
  return '< 1m';
};

const totalExperience = (assignments: Assignment[]): string => {
  if (assignments.length === 0) return '—';
  const sorted = [...assignments].sort((a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime());
  const earliest = new Date(sorted[0].startDate);
  const now = new Date();
  const years = Math.floor((now.getTime() - earliest.getTime()) / (365.25 * 24 * 60 * 60 * 1000));
  if (years < 1) return '< 1 year';
  return `${years} year${years !== 1 ? 's' : ''}`;
};

const StatusPill: React.FC<{ status: string }> = ({ status }) => (
  <Pill tone={statusTone(status)}>{status.charAt(0).toUpperCase() + status.slice(1)}</Pill>
);

/** Media kind from a MIME type, for the file-type hue of non-image tiles */
const mediaKindOf = (mimeType?: string): MediaKind => {
  const type = (mimeType || '').toLowerCase();
  if (type.startsWith('image/')) return 'image';
  if (type.startsWith('video/')) return 'video';
  if (type.startsWith('audio/')) return 'audio';
  if (type === 'application/pdf') return 'pdf';
  if (type.startsWith('text/') || /word|document|sheet|excel|presentation|powerpoint|rtf|opendocument/.test(type)) return 'document';
  return 'other';
};

const MEDIA_ICON: Record<MediaKind, LucideIcon> = {
  image: ImageIcon,
  video: Film,
  audio: Music,
  pdf: FileText,
  document: FileText,
  other: FileIcon,
};

/** "media-journalist-influencer" → "Media journalist influencer" */
const categoryLabel = (category: string) => {
  const text = category.replace(/[-_]+/g, ' ').trim();
  return text.charAt(0).toUpperCase() + text.slice(1);
};

const getInitials = (firstName?: string, lastName?: string) =>
  `${(firstName || '').charAt(0)}${(lastName || '').charAt(0)}`.toUpperCase();

const orgNameOf = (assignment?: Assignment) => assignment?.organizationName || (assignment as any)?.organization?.name;

/* ========== social links ========== */

interface SocialLinkDef {
  key: string;
  label: string;
  icon: LucideIcon;
  getUrl: (stakeholder: Stakeholder) => string | undefined;
  getHref: (url: string) => string;
}

const socialLinks: SocialLinkDef[] = [
  { key: 'linkedin', label: 'LinkedIn', icon: Linkedin, getUrl: (s) => s.linkedinUrl, getHref: (u) => normalizeUrl(u) },
  { key: 'twitter', label: 'X', icon: Twitter, getUrl: (s) => s.twitterUrl, getHref: (u) => normalizeUrl(u) },
  { key: 'facebook', label: 'Facebook', icon: Facebook, getUrl: (s) => s.facebookUrl, getHref: (u) => normalizeUrl(u) },
  { key: 'telegram', label: 'Telegram', icon: Send, getUrl: (s) => s.telegramUrl, getHref: (u) => normalizeUrl(u) },
  { key: 'whatsapp', label: 'WhatsApp', icon: MessageCircle, getUrl: (s) => s.whatsapp, getHref: (u) => whatsappLink(u) },
];

/* ========== small pieces ========== */

const BackLink: React.FC = () => (
  <Link
    to="/stakeholders"
    className="-ml-1 inline-flex h-7 items-center gap-0.5 rounded-full pl-0.5 pr-2 text-[13px] text-neutral-500 transition-colors hover:text-ink"
  >
    <ChevronLeft className="h-4 w-4" strokeWidth={1.75} />
    Stakeholders
  </Link>
);

const ProfileAvatar: React.FC<{ stakeholder: Stakeholder; name: string; className: string }> = ({
  stakeholder,
  name,
  className,
}) => {
  const photo = 'h-full w-full object-cover';
  const image = stakeholder.profileImageId ? (
    <AuthImage
      mediaId={stakeholder.profileImageId}
      alt={name}
      className={photo}
      fallback={getInitials(stakeholder.firstName, stakeholder.lastName)}
    />
  ) : stakeholder.profileImageUrl ? (
    <img src={stakeholder.profileImageUrl} alt={name} className={photo} />
  ) : undefined;
  return (
    <Avatar
      name={name}
      initials={getInitials(stakeholder.firstName, stakeholder.lastName) || undefined}
      kind="stakeholder"
      size="lg"
      image={image}
      className={className}
    />
  );
};

/** Non-image file placeholder; the icon takes the file type's hue */
const FileTile: React.FC<{ mimeType?: string }> = ({ mimeType }) => {
  const kind = mediaKindOf(mimeType);
  const Icon = MEDIA_ICON[kind];
  return (
    <div className="flex aspect-[4/3] w-full items-center justify-center bg-neutral-50">
      <Icon className={`h-6 w-6 ${MEDIA_TONE[kind].text}`} strokeWidth={1.5} />
    </div>
  );
};

const FactRow: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div className="flex min-h-[40px] items-center justify-between gap-4 px-4 py-2 text-[13px]">
    <span className="flex-shrink-0 text-neutral-500">{label}</span>
    <span className="flex min-w-0 items-center justify-end text-right text-ink">{children}</span>
  </div>
);

const valueLink = 'truncate transition-colors hover:text-primary-700';

const PageSkeleton: React.FC = () => (
  <Page>
    <Skeleton className="h-7 w-28 rounded-full" />
    <div className="mt-3 flex flex-col items-center gap-3 sm:mt-2 sm:flex-row sm:gap-4">
      <Skeleton className="h-[72px] w-[72px] rounded-full sm:h-12 sm:w-12" />
      <div className="flex flex-col items-center gap-2 sm:items-start">
        <Skeleton className="h-6 w-48" />
        <Skeleton className="h-4 w-32" />
      </div>
    </div>
    <div className="mt-6 flex gap-5 border-b border-neutral-200/70 pb-3">
      {['w-16', 'w-12', 'w-10', 'w-10'].map((width, i) => (
        <Skeleton key={i} className={`h-4 ${width}`} />
      ))}
    </div>
    <div className="mt-5 grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_320px]">
      <div className="space-y-3">
        <Skeleton className="h-4 w-20" />
        <Skeleton className="h-16 w-full" />
        <Skeleton className="mt-6 h-4 w-20" />
        <Skeleton className="h-44 w-full rounded-2xl" />
      </div>
      <div className="space-y-3">
        <Skeleton className="h-4 w-20" />
        <Skeleton className="h-52 w-full rounded-2xl" />
      </div>
    </div>
  </Page>
);

const TABS = ['overview', 'media', 'wiki', 'news', 'monitoring'] as const;
type TabValue = (typeof TABS)[number];

/* One codec per parameter, made once: the URL state reads them on every render */
const TAB = asOneOf(TABS);

/* ========== MAIN COMPONENT ========== */

export const StakeholderDetailPage: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { colorOf } = useTaxonomyColors();

  const [stakeholder, setStakeholder] = useState<Stakeholder | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [taggedMedia, setTaggedMedia] = useState<MediaAsset[]>([]);
  const [wikiMedia, setWikiMedia] = useState<MediaAsset[]>([]);
  const [wikiPages, setWikiPages] = useState<WikiPage[]>([]);
  // Which section is open is in the address bar, so a link opens the profile
  // where the sender was looking; a section this record does not have falls
  // back to the overview
  const [activeTab, setActiveTab] = useUrlState<TabValue>('tab', 'overview', TAB);
  /** Kept beside the record so the header marker updates without a refetch */
  const [confidentiality, setConfidentiality] = useState<Confidentiality | null>(null);
  const [generatorKey, setGeneratorKey] = useState(0);
  const [wikiCreateMode, setWikiCreateMode] = useState(false);
  const [influenceScores, setInfluenceScores] = useState<InfluenceScore[]>([]);
  const [influenceCategories, setInfluenceCategories] = useState<InfluenceCategory[]>([]);
  const [loadingInfluence, setLoadingInfluence] = useState(false);
  const [stakeholderAssignments, setStakeholderAssignments] = useState<Assignment[]>([]);
  const [showAllAssignments, setShowAllAssignments] = useUrlState('allRoles', false, asFlag);

  // The report and intelligence generators own their trigger buttons; they stay mounted
  // off-screen and are opened from the page's menus.
  const reportHostRef = useRef<HTMLDivElement>(null);
  const intelligenceHostRef = useRef<HTMLDivElement>(null);
  const openFrom = (host: React.RefObject<HTMLDivElement | null>) => host.current?.querySelector('button')?.click();

  useEffect(() => {
    const load = async () => {
      if (!id) return;
      try {
        setLoading(true);
        setError(null);
        const [s, m, w, a] = await Promise.all([
          stakeholderService.getStakeholder(id),
          mediaService.getMediaAssets({ stakeholderId: id }),
          wikiService.getWikiPagesByEntity('stakeholder', id),
          assignmentService.getByStakeholder(id),
        ]);
        setStakeholder(s);
        setTaggedMedia(m || []);
        setWikiPages(w || []);
        setStakeholderAssignments(a || []);

        try {
          setLoadingInfluence(true);
          const influenceData = await getEntityInfluenceData('stakeholder', id);
          setInfluenceScores(influenceData.scores);
          setInfluenceCategories(influenceData.categories);
        } catch {
          setInfluenceScores([]);
          setInfluenceCategories([]);
        } finally {
          setLoadingInfluence(false);
        }

        const firstWiki = (w || [])[0];
        if (firstWiki?.content) {
          const mediaIds = extractMediaIdsFromContent(firstWiki.content);
          if (mediaIds.length) {
            const fetched = await Promise.all(
              mediaIds.map(async (mid) => {
                try { return await mediaService.getMediaAsset(mid); } catch { return null as any; }
              })
            );
            setWikiMedia(fetched.filter(Boolean));
          } else {
            setWikiMedia([]);
          }
        } else {
          setWikiMedia([]);
        }
      } catch (err: any) {
        setError(err?.response?.data?.message || 'Failed to load stakeholder');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id]);

  /* ---- derived data ---- */

  const fullName = `${stakeholder?.firstName ?? ''} ${stakeholder?.lastName ?? ''}`.trim();

  const currentAssignment = useMemo(
    () => stakeholderAssignments.find((a) => a.isCurrent) || stakeholderAssignments[0],
    [stakeholderAssignments]
  );

  const sortedAssignments = useMemo(() => {
    return [...stakeholderAssignments].sort((a, b) => {
      if (a.isCurrent !== b.isCurrent) return a.isCurrent ? -1 : 1;
      return new Date(b.startDate).getTime() - new Date(a.startDate).getTime();
    });
  }, [stakeholderAssignments]);

  const visibleAssignments = showAllAssignments ? sortedAssignments : sortedAssignments.slice(0, 4);

  const canUpdate = useMemo(() => {
    if (!user || !stakeholder) return false;
    if (['admin', 'super_admin', 'top_owner'].includes(user.role)) return true;
    return user.role === 'user' && stakeholder.createdBy === user.id;
  }, [user, stakeholder]);

  /** Applying a suggested update rewrites roles, so only reviewers see them */
  const canReviewUpdates = !!user && ['admin', 'super_admin'].includes(user.role);

  const reloadAssignments = () => {
    if (!id) return;
    void assignmentService
      .getByStakeholder(id)
      .then((a) => setStakeholderAssignments(a || []))
      .catch(() => undefined);
  };

  const canArchive = useMemo(() => {
    if (!user || !stakeholder) return false;
    if (['admin', 'super_admin'].includes(user.role)) return true;
    if (user.role === 'top_owner') return stakeholder.status !== 'approved';
    return user.role === 'user' && stakeholder.createdBy === user.id;
  }, [user, stakeholder]);

  const isAdmin = !!user && (user.role === 'admin' || user.role === 'super_admin');

  const activeSocials = useMemo(() => {
    if (!stakeholder) return [];
    return socialLinks.filter((sl) => !!sl.getUrl(stakeholder));
  }, [stakeholder]);

  const missingMedia = useMissingMedia();

  const allRelevantMedia = useMemo<MediaAsset[]>(() => {
    const map = new Map<string, MediaAsset>();
    [...(taggedMedia || []), ...(wikiMedia || [])].forEach((a) => {
      if (a && !map.has(a.id)) map.set(a.id, a);
    });
    // A file that has gone from storage is not offered at all — a tile that
    // cannot draw says nothing, and the count would be wrong besides
    return Array.from(map.values()).filter((a) => !missingMedia(a.id));
  }, [taggedMedia, wikiMedia, missingMedia]);

  const previewImages = useMemo<MediaAsset[]>(() => {
    return allRelevantMedia.filter((a) => a?.mimeType?.startsWith('image/')).slice(0, 5);
  }, [allRelevantMedia]);

  const wikiSnippet = useMemo(() => (wikiPages[0]?.content ?? '').trim(), [wikiPages]);

  const influenceScore = stakeholder?.influenceScore;

  // Strongest influence area, shown next to the status
  const dominantInfluence = useMemo(() => {
    if (!influenceScores.length) return null;
    const top = influenceScores.reduce((best, score) => (score.score > best.score ? score : best));
    const category = top.category || influenceCategories.find((c) => c.id === top.categoryId);
    return category ? { label: category.label, color: category.color, score: top.score } : null;
  }, [influenceScores, influenceCategories]);

  /** The areas their influence has been assessed in, strongest first */
  const influenceAreas = useMemo(
    () =>
      influenceScores.flatMap((score) => {
        const category = score.category || influenceCategories.find((c) => c.id === score.categoryId);
        return category
          ? [{ label: category.label, color: category.color ?? null, score: score.score }]
          : [];
      }),
    [influenceScores, influenceCategories],
  );

  const [confirmArchive, setConfirmArchive] = useState(false);
  const [notice, setNotice] = useState<{ open: boolean; title: string; message: React.ReactNode }>({ open: false, title: '', message: '' });
  const [showAssignmentManager, setShowAssignmentManager] = useState(false);

  /* ========== RENDER ========== */

  if (loading) {
    return (
      <DashboardLayout>
        <PageSkeleton />
      </DashboardLayout>
    );
  }

  if (error) {
    return (
      <DashboardLayout>
        <Page>
          <BackLink />
          <p
            role="alert"
            className="mt-3 flex items-center gap-2 rounded-xl bg-primary-50 px-3.5 py-2.5 text-[13px] text-primary-700"
          >
            <CircleAlert className="h-4 w-4 flex-shrink-0" strokeWidth={1.75} />
            {error}
          </p>
        </Page>
      </DashboardLayout>
    );
  }

  if (!stakeholder) return null;

  const primaryEmail = stakeholder.professionalEmail || stakeholder.personalEmail;
  const primaryPhone = stakeholder.professionalPhone || stakeholder.personalPhone;
  const currentOrgName = orgNameOf(currentAssignment);

  const menuItems: MenuItem[] = [
    { label: 'Report', icon: FileText, onSelect: () => openFrom(reportHostRef) },
    ...(isAdmin ? [{ label: 'Intelligence', icon: Sparkles, onSelect: () => openFrom(intelligenceHostRef) }] : []),
    ...(canArchive ? [{ label: 'Archive', icon: Trash2, danger: true, onSelect: () => setConfirmArchive(true) }] : []),
  ];

  const actions = (
    <>
      {canUpdate && (
        <Button size="sm" variant="secondary" onClick={() => navigate(`/stakeholders/${id}/edit`)}>
          <Pencil className="h-3.5 w-3.5" strokeWidth={1.75} />
          Edit
        </Button>
      )}
      <Menu
        items={menuItems}
        trigger={(props) => (
          <IconButton label="More actions" size="sm" className="ring-1 ring-inset ring-neutral-200" {...props}>
            <MoreHorizontal className="h-4 w-4" strokeWidth={1.75} />
          </IconButton>
        )}
      />
    </>
  );

  // Phones: large tappable shortcuts under the name, as in a native contact card
  const quickActions: Array<{ key: string; label: string; icon: LucideIcon; href: string; external?: boolean }> = [
    ...(primaryEmail ? [{ key: 'email', label: 'Email', icon: Mail, href: `mailto:${primaryEmail}` }] : []),
    ...(primaryPhone ? [{ key: 'call', label: 'Call', icon: Phone, href: `tel:${primaryPhone}` }] : []),
    ...activeSocials.map((sl) => ({
      key: sl.key,
      label: sl.label,
      icon: sl.icon,
      href: sl.getHref(sl.getUrl(stakeholder)!),
      external: true,
    })),
  ].slice(0, 4);

  /* ---- overview blocks ---- */

  const bothEmails = !!stakeholder.professionalEmail && !!stakeholder.personalEmail;
  const bothPhones = !!stakeholder.professionalPhone && !!stakeholder.personalPhone;

  const details = (
    <section>
      <SectionHeader title="Details" />
      <Card divided>
        {stakeholder.professionalEmail && (
          <FactRow label={bothEmails ? 'Work email' : 'Email'}>
            <a href={`mailto:${stakeholder.professionalEmail}`} className={valueLink}>{stakeholder.professionalEmail}</a>
          </FactRow>
        )}
        {stakeholder.personalEmail && (
          <FactRow label={bothEmails ? 'Personal email' : 'Email'}>
            <a href={`mailto:${stakeholder.personalEmail}`} className={valueLink}>{stakeholder.personalEmail}</a>
          </FactRow>
        )}
        {stakeholder.professionalPhone && (
          <FactRow label={bothPhones ? 'Work phone' : 'Phone'}>
            <a href={`tel:${stakeholder.professionalPhone}`} className={`${valueLink} tabular-nums`}>{stakeholder.professionalPhone}</a>
          </FactRow>
        )}
        {stakeholder.personalPhone && (
          <FactRow label={bothPhones ? 'Personal phone' : 'Phone'}>
            <a href={`tel:${stakeholder.personalPhone}`} className={`${valueLink} tabular-nums`}>{stakeholder.personalPhone}</a>
          </FactRow>
        )}
        {stakeholder.location && (
          <FactRow label="Location">
            <span className="truncate">{stakeholder.location}</span>
          </FactRow>
        )}
        {stakeholder.category && (
          <FactRow label="Category">
            <CategoryPill
              label={categoryLabel(stakeholder.category)}
              color={colorOf('stakeholder_category', stakeholder.category)}
              title={stakeholder.category}
            />
          </FactRow>
        )}
        {activeSocials.length > 0 && (
          <FactRow label="Links">
            <span className="-mr-1.5 flex items-center gap-0.5">
              {activeSocials.map((sl) => {
                const href = sl.getHref(sl.getUrl(stakeholder)!);
                const Icon = sl.icon;
                return (
                  <a
                    key={sl.key}
                    href={href}
                    target="_blank"
                    rel="noreferrer"
                    aria-label={sl.label}
                    title={`${sl.label} · ${prettyUrl(href)}`}
                    className="inline-flex h-7 w-7 items-center justify-center rounded-full text-neutral-500 transition-colors hover:bg-neutral-100 hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-600/40"
                  >
                    <Icon className="h-4 w-4" strokeWidth={1.75} />
                  </a>
                );
              })}
            </span>
          </FactRow>
        )}
      </Card>
      <p className="mt-2 px-1 text-[12px] text-neutral-400">
        Added {formatFullDate(stakeholder.createdAt)}
        {stakeholder.updatedAt && ` · Updated ${formatFullDate(stakeholder.updatedAt)}`}
      </p>
    </section>
  );

  const roleCount = stakeholderAssignments.length;

  return (
    <DashboardLayout>
      <Page>
        {/* ==================== HEADER ==================== */}
        <header className="mb-5">
          <div className="flex min-h-[32px] items-center justify-between gap-2">
            <BackLink />
            <div className="flex items-center gap-2 sm:hidden">{actions}</div>
          </div>

          <div className="mt-3 flex flex-col items-center gap-3 text-center sm:mt-2 sm:flex-row sm:gap-4 sm:text-left">
            <ProfileAvatar
              stakeholder={stakeholder}
              name={fullName}
              className="!h-[72px] !w-[72px] !text-[24px] sm:!h-12 sm:!w-12 sm:!text-[15px]"
            />
            <div className="min-w-0 max-w-full flex-1">
              <h1 className="truncate text-[24px] font-normal leading-tight tracking-[-0.03em] text-ink">
                {fullName || '—'}
              </h1>
              <p className="mt-1 flex flex-wrap items-center justify-center gap-x-2 gap-y-1 text-[13px] text-neutral-500 sm:justify-start">
                {currentAssignment && (
                  <span className="min-w-0 truncate">
                    {currentAssignment.role}
                    {currentOrgName && (
                      <>
                        {' · '}
                        <Link
                          to={`/organizations/${currentAssignment.organizationId}`}
                          className="transition-colors hover:text-ink"
                        >
                          {currentOrgName}
                        </Link>
                      </>
                    )}
                  </span>
                )}
                {/* An approved record is the normal case; saying so is noise */}
                {stakeholder.status !== 'approved' && <StatusPill status={stakeholder.status} />}
                <LockMarker
                  level={confidentiality ?? stakeholder.confidentiality ?? 'normal'}
                  variant="pill"
                />
                {dominantInfluence && (
                  <CategoryPill
                    label={dominantInfluence.label}
                    color={dominantInfluence.color}
                    title={`Strongest influence · ${dominantInfluence.score}/100`}
                  />
                )}
              </p>
            </div>
            <div className="hidden flex-shrink-0 items-center gap-2 sm:flex">{actions}</div>
          </div>

          {quickActions.length > 0 && (
            <div
              className="mt-5 grid gap-2 sm:hidden"
              style={{ gridTemplateColumns: `repeat(${quickActions.length}, minmax(0, 1fr))` }}
            >
              {quickActions.map(({ key, label, icon: Icon, href, external }) => (
                <a
                  key={key}
                  href={href}
                  {...(external ? { target: '_blank', rel: 'noreferrer' } : {})}
                  className="flex h-14 flex-col items-center justify-center gap-1 rounded-2xl bg-neutral-100 text-[11px] text-ink transition-colors active:bg-neutral-200"
                >
                  <Icon className="h-[18px] w-[18px]" strokeWidth={1.75} />
                  {label}
                </a>
              ))}
            </div>
          )}
        </header>

        {/* ==================== TABS ==================== */}
        <Tabs<TabValue>
          label="Stakeholder sections"
          className="mb-5"
          value={activeTab}
          onChange={setActiveTab}
          items={[
            { value: 'overview', label: 'Overview' },
            { value: 'media', label: 'Media', count: allRelevantMedia.length },
            { value: 'wiki', label: 'Wiki', count: wikiPages.length },
            { value: 'news', label: 'News' },
            { value: 'monitoring', label: 'Monitoring' },
          ]}
        />

        {/* ==================== OVERVIEW TAB ==================== */}
        {activeTab === 'overview' && (
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_320px]">
            {/* ---- main column ---- */}
            <div className="min-w-0 space-y-6">
              {stakeholder.bio && (
                <section>
                  <SectionHeader title="About" />
                  <p className="whitespace-pre-line text-[14px] leading-relaxed text-neutral-700">{stakeholder.bio}</p>
                </section>
              )}

              <div className="lg:hidden">{details}</div>

              {/* What research thinks has changed about this person */}
              <StakeholderUpdates
                stakeholderId={id!}
                canEdit={canReviewUpdates}
                onApplied={reloadAssignments}
              />

              {/* Career */}
              <section>
                <SectionHeader
                  title="Career"
                  aside={
                    <>
                      {roleCount > 0 && !showAssignmentManager && (
                        <span className="tabular-nums">
                          {roleCount} {roleCount === 1 ? 'role' : 'roles'} · {totalExperience(stakeholderAssignments)}
                        </span>
                      )}
                      {canUpdate && (
                        <Button size="xs" variant="ghost" onClick={() => setShowAssignmentManager(!showAssignmentManager)}>
                          {showAssignmentManager ? 'Done' : 'Manage'}
                        </Button>
                      )}
                    </>
                  }
                />
                {showAssignmentManager ? (
                  <Card>
                    <AssignmentManager
                      stakeholderId={id!}
                      assignments={stakeholderAssignments}
                      onAssignmentsChange={setStakeholderAssignments}
                      canEdit={canUpdate}
                    />
                  </Card>
                ) : sortedAssignments.length === 0 ? (
                  <Card>
                    <EmptyState
                      icon={Briefcase}
                      title="No roles yet"
                      className="py-8"
                      action={
                        canUpdate ? (
                          <Button size="sm" variant="secondary" onClick={() => setShowAssignmentManager(true)}>
                            Add role
                          </Button>
                        ) : undefined
                      }
                    />
                  </Card>
                ) : (
                  <Card divided>
                    {visibleAssignments.map((assignment) => (
                      <div key={assignment.id} className="flex items-center gap-3 px-4 py-3">
                        <Avatar name={orgNameOf(assignment)} kind="organization" size="md" />
                        <div className="min-w-0 flex-1">
                          <div className="flex min-w-0 items-center gap-2">
                            <p className="truncate text-[13px] text-ink">{assignment.role}</p>
                            {assignment.isCurrent && <Pill tone="green">Current</Pill>}
                          </div>
                          <p className="mt-0.5 truncate text-[12px] text-neutral-500">
                            <Link
                              to={`/organizations/${assignment.organizationId}`}
                              className="transition-colors hover:text-ink"
                            >
                              {orgNameOf(assignment) || 'Unknown'}
                            </Link>
                            {assignment.department && ` · ${assignment.department}`}
                          </p>
                          {/* Phones: dates under the role, so the title keeps the width */}
                          <p className="mt-0.5 truncate text-[12px] tabular-nums text-neutral-400 sm:hidden">
                            {formatDate(assignment.startDate)} – {assignment.isCurrent ? 'Present' : formatDate(assignment.endDate)}
                            {' · '}
                            {calculateDuration(assignment.startDate, assignment.endDate)}
                          </p>
                        </div>
                        <div className="hidden flex-shrink-0 text-right text-[12px] tabular-nums text-neutral-400 sm:block">
                          <p className="text-neutral-500">
                            {formatDate(assignment.startDate)} – {assignment.isCurrent ? 'Present' : formatDate(assignment.endDate)}
                          </p>
                          <p className="mt-0.5">{calculateDuration(assignment.startDate, assignment.endDate)}</p>
                        </div>
                      </div>
                    ))}
                    {sortedAssignments.length > 4 && (
                      <button
                        type="button"
                        onClick={() => setShowAllAssignments(!showAllAssignments)}
                        className="flex h-10 w-full items-center justify-center text-[13px] text-neutral-500 transition-colors hover:bg-neutral-50 hover:text-ink focus-visible:bg-neutral-50 focus-visible:outline-none"
                      >
                        {showAllAssignments ? 'Show less' : `Show all ${sortedAssignments.length}`}
                      </button>
                    )}
                  </Card>
                )}
              </section>

              {/* Who they are tied to, and who can reach them */}
              <ProfileNetwork id={id} />

              {/* Influence */}
              <section>
                <SectionHeader
                  title="Influence"
                  aside={
                    influenceScores.length > 0 ? (
                      <span className="tabular-nums">
                        {influenceScores.length} {influenceScores.length === 1 ? 'category' : 'categories'}
                      </span>
                    ) : undefined
                  }
                />
                <Card padded>
                  {typeof influenceScore === 'number' && (influenceScores.length > 0 || influenceScore > 0) && (
                    <div className="mb-4">
                      <div className="flex items-baseline justify-between gap-3">
                        <span className="text-[12px] text-neutral-500">Overall</span>
                        <span className="text-[22px] leading-none tracking-[-0.02em] tabular-nums text-ink">
                          {influenceScore}
                          <span className="text-[13px] text-neutral-400">/100</span>
                        </span>
                      </div>
                      <div
                        className="mt-2.5 h-1 overflow-hidden rounded-full bg-neutral-100"
                        role="meter"
                        aria-label="Overall influence"
                        aria-valuemin={0}
                        aria-valuemax={100}
                        aria-valuenow={influenceScore}
                      >
                        <div
                          className="h-full rounded-full bg-ink transition-[width] duration-500"
                          style={{ width: `${Math.max(0, Math.min(100, influenceScore))}%` }}
                        />
                      </div>
                    </div>
                  )}

                  {loadingInfluence ? (
                    <div className="flex h-40 items-center justify-center text-neutral-400">
                      <Spinner className="h-5 w-5" />
                    </div>
                  ) : influenceScores.length > 0 ? (
                    <div className="overflow-x-auto">
                      <InfluenceLineChart data={influenceScores} categories={influenceCategories} width={600} height={280} />
                    </div>
                  ) : (
                    <p className="text-[13px] text-neutral-400">No influence data</p>
                  )}
                </Card>
              </section>

              {/* Issues this person has taken a position on */}
              <StakeholderIssuesSection stakeholderId={id} />
            </div>

            {/* ---- side column ---- */}
            <div className="min-w-0 space-y-6">
              <div className="hidden lg:block">{details}</div>

              <AreasOfInterest subjectType="stakeholder" subjectId={id} influence={influenceAreas} />

              {/* Media preview */}
              <section>
                <SectionHeader
                  title="Media"
                  aside={
                    allRelevantMedia.length > 0 ? (
                      <Button size="xs" variant="ghost" onClick={() => setActiveTab('media')}>
                        View all
                      </Button>
                    ) : undefined
                  }
                />
                {previewImages.length > 0 ? (
                  <Card className="p-1.5">
                    <div className="grid grid-cols-3 gap-1.5">
                      {previewImages.map((asset) => (
                        <button
                          key={asset.id}
                          type="button"
                          onClick={() => setActiveTab('media')}
                          className="aspect-square overflow-hidden rounded-xl bg-neutral-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-600/40"
                          aria-label={asset.originalName}
                        >
                          <AuthImage mediaId={asset.id} alt={asset.originalName} className="h-full w-full object-cover" />
                        </button>
                      ))}
                      {allRelevantMedia.length > 5 && (
                        <button
                          type="button"
                          onClick={() => setActiveTab('media')}
                          className="flex aspect-square items-center justify-center rounded-xl bg-neutral-100 text-[13px] tabular-nums text-neutral-500 transition-colors hover:text-ink"
                        >
                          +{allRelevantMedia.length - 5}
                        </button>
                      )}
                    </div>
                  </Card>
                ) : (
                  <Card padded>
                    <p className="text-[13px] text-neutral-400">
                      {allRelevantMedia.length > 0 ? `${allRelevantMedia.length} files` : 'No media'}
                    </p>
                  </Card>
                )}
              </section>

              {/* Wiki preview */}
              <section>
                <SectionHeader
                  title="Wiki"
                  aside={
                    wikiPages.length > 0 ? (
                      <Button size="xs" variant="ghost" onClick={() => setActiveTab('wiki')}>
                        Open
                      </Button>
                    ) : undefined
                  }
                />
                <WikiExcerpt html={wikiSnippet} onOpen={() => setActiveTab('wiki')} empty="No wiki yet" />
              </section>

              {/* Who can see this record */}
              <section>
                <ConfidentialityPanel
                  entityType="stakeholder"
                  entityId={stakeholder.id}
                  onChanged={(summary) => setConfidentiality(summary.confidentiality)}
                />
              </section>
            </div>
          </div>
        )}

        {/* ==================== MEDIA TAB ==================== */}
        {activeTab === 'media' && (
          allRelevantMedia.length > 0 ? (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
              {allRelevantMedia.map((asset) => (
                <Card key={asset.id}>
                  {mediaService.isImageFile(asset.mimeType) ? (
                    <AuthImage mediaId={asset.id} alt={asset.originalName} className="aspect-[4/3] w-full object-cover" />
                  ) : (
                    <FileTile mimeType={asset.mimeType} />
                  )}
                  <div className="border-t border-neutral-200/70 px-3 py-2.5">
                    <p className="truncate text-[13px] text-ink" title={asset.originalName}>{asset.originalName}</p>
                    <p className="mt-0.5 text-[12px] text-neutral-400">{formatFullDate(asset.createdAt)}</p>
                  </div>
                </Card>
              ))}
            </div>
          ) : (
            <Card>
              <EmptyState icon={ImageIcon} title="No media" />
            </Card>
          )
        )}

        {/* ==================== WIKI TAB ==================== */}
        {activeTab === 'wiki' && (
          <div>
            <div className="mb-4 flex items-center justify-end gap-2">
              <Button size="sm" variant="secondary" onClick={() => setWikiCreateMode(true)}>
                <FilePlus className="h-4 w-4" strokeWidth={1.75} />
                New page
              </Button>
              {isAdmin && (
                <Button size="sm" variant="dark" onClick={() => openFrom(intelligenceHostRef)}>
                  <Sparkles className="h-4 w-4" strokeWidth={1.75} />
                  Generate
                </Button>
              )}
            </div>
            <WikiPageManager
              key={generatorKey}
              entityType="stakeholder"
              entityId={id!}
              entityName={fullName}
              externalCreateMode={wikiCreateMode}
              onCreateModeChange={setWikiCreateMode}
            />
          </div>
        )}

        {/* ==================== NEWS TAB ==================== */}
        {activeTab === 'news' && (
          <StakeholderNewsTab
            entityId={id!}
            entityName={fullName}
          />
        )}

        {/* ==================== MONITORING TAB ==================== */}
        {activeTab === 'monitoring' && (
          <EntityMonitoringPanel subjectType="stakeholder" subjectId={id!} subjectLabel={fullName} />
        )}

        {/* Generators: their own triggers are hidden, their dialogs open from the menus above */}
        <div ref={reportHostRef} className="contents [&>button:first-child]:hidden">
          <ReportGenerator
            entity={stakeholder}
            entityType="stakeholder"
            assignments={stakeholderAssignments}
            media={allRelevantMedia}
            wikiPages={wikiPages}
            influenceScores={influenceScores}
            influenceCategories={influenceCategories}
          />
        </div>
        <div ref={intelligenceHostRef} className="contents [&>button:first-child]:hidden">
          <IntelligenceReportGenerator
            entityType="stakeholder"
            entityId={id!}
            entityName={fullName}
            onComplete={() => {
              setGeneratorKey(k => k + 1);
              wikiService.getWikiPagesByEntity('stakeholder', id!).then(w => setWikiPages(w || []));
              setActiveTab('wiki');
            }}
          />
        </div>
      </Page>

      {/* ---- Modals ---- */}
      <ConfirmModal
        open={confirmArchive}
        title="Archive stakeholder"
        message={<span>Archive <span className="text-ink">{fullName}</span>? This removes the record.</span>}
        confirmText="Archive"
        onCancel={() => setConfirmArchive(false)}
        onConfirm={async () => {
          if (!id) return;
          try {
            await stakeholderService.deleteStakeholder(id);
            navigate('/stakeholders');
          } catch (err: any) {
            setNotice({ open: true, title: 'Archive Failed', message: err?.response?.data?.message || 'Failed to archive stakeholder' });
          }
        }}
      />
      <MessageModal open={notice.open} title={notice.title} message={notice.message} onClose={() => setNotice(n => ({ ...n, open: false }))} />
    </DashboardLayout>
  );
};
